import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarClock, Loader2, X, Check } from "lucide-react";
import { SlotPicker } from "@/components/SlotPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const field = "w-full px-3 py-2 rounded-md bg-black border border-white/15 text-white text-sm focus:border-[#F2B705] outline-none [color-scheme:dark]";

const detail = (err, fb) => {
  const d = err?.response?.data?.detail;
  return typeof d === "string" ? d : fb;
};

// Inline manual pickup/return date + time editor for a booking row.
export default function EditBookingDates({ booking, token, onSaved }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [applyTotal, setApplyTotal] = useState(true);
  const [d, setD] = useState({
    pickup_date: (booking.pickup_date || "").slice(0, 10),
    pickup_time: booking.pickup_time || "",
    return_date: (booking.return_date || "").slice(0, 10),
    return_time: booking.return_time || "",
  });

  const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
  const oldDays = Math.max(Number(booking.days) || 1, 1);
  const oldTotal = round2(Number(booking.total_price) || 0);
  const daily = Number(booking.daily_rate) || 0;
  const waiverPd = (booking.add_damage_protection || booking.waiver_enabled) ? 15 : 0;
  const perDay = daily > 0 ? round2(daily + waiverPd) : round2(oldTotal / oldDays);

  let newDays = null, delta = 0, newTotal = oldTotal;
  const pu = d.pickup_date ? new Date(`${d.pickup_date}T${d.pickup_time || "10:00"}`) : null;
  const rt = d.return_date ? new Date(`${d.return_date}T${d.return_time || d.pickup_time || "10:00"}`) : null;
  if (pu && rt && !isNaN(pu) && !isNaN(rt) && rt >= pu) {
    newDays = Math.max(Math.ceil((rt - pu) / 86400000), 1);
    delta = round2(perDay * (newDays - oldDays));
    newTotal = round2(oldTotal + delta);
  }
  const daysChanged = newDays !== null && newDays !== oldDays;
  const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const save = async (force = false) => {
    setBusy(true);
    try {
      const { data } = await axios.patch(`${API}/bookings/${booking.id}/dates`, { ...d, force, recalc: daysChanged && applyTotal }, auth);
      toast.success(data?.recalc
        ? `Dates updated · total recalculated to ${money(data.recalc.new_total)} (${data.recalc.new_days} days, package pricing).`
        : "Booking dates updated.");
      setOpen(false);
      onSaved?.();
    } catch (e) {
      if (e?.response?.status === 409 && !force) {
        if (window.confirm(detail(e, "These dates overlap another reservation on this car. Save anyway?"))) return save(true);
      } else {
        toast.error(detail(e, "Could not update dates"));
      }
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button
        data-testid={`edit-dates-btn-${booking.id}`}
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 text-xs text-white/50 hover:text-[#F2B705] border border-white/10 hover:border-[#F2B705]/40 rounded-md px-2 py-0.5 transition-colors"
      >
        <CalendarClock size={12} /> Edit dates
      </button>
    );
  }

  return (
    <div className="mt-2 w-full rounded-lg bg-[#0C0C0C] border border-[#F2B705]/25 p-3" data-testid={`edit-dates-panel-${booking.id}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-white/80 flex items-center gap-1.5"><CalendarClock size={13} className="text-[#F2B705]" /> Edit Pickup & Return</span>
        <button onClick={() => setOpen(false)} className="text-white/40 hover:text-white" data-testid={`edit-dates-close-${booking.id}`}><X size={15} /></button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div>
          <label className="text-[10px] text-white/45 block mb-1">Pickup date</label>
          <input type="date" className={field} value={d.pickup_date} data-testid={`edit-pickup-date-${booking.id}`}
            onChange={(e) => setD((s) => ({ ...s, pickup_date: e.target.value }))} />
        </div>
        <div>
          <label className="text-[10px] text-white/45 block mb-1">Pickup time</label>
          <SlotPicker testid={`edit-pickup-time-${booking.id}`} date={d.pickup_date} value={d.pickup_time}
            onChange={(v) => setD((s) => ({ ...s, pickup_time: v }))} className={field} />
        </div>
        <div>
          <label className="text-[10px] text-white/45 block mb-1">Return date</label>
          <input type="date" className={field} value={d.return_date} data-testid={`edit-return-date-${booking.id}`}
            onChange={(e) => setD((s) => ({ ...s, return_date: e.target.value }))} />
        </div>
        <div>
          <label className="text-[10px] text-white/45 block mb-1">Return time</label>
          <SlotPicker testid={`edit-return-time-${booking.id}`} date={d.return_date} value={d.return_time}
            onChange={(v) => setD((s) => ({ ...s, return_time: v }))} className={field} />
        </div>
      </div>
      {daysChanged && (
        <div className="mt-3 rounded-lg border border-[#F2B705]/25 bg-[#F2B705]/[0.06] p-3" data-testid={`edit-dates-recalc-${booking.id}`}>
          <div className="flex items-center justify-between gap-3 text-xs">
            <span className="text-white/70">
              Rental length <b className="text-white">{oldDays}</b> → <b className="text-[#F2B705]">{newDays}</b> day{newDays === 1 ? "" : "s"}
              <span className="text-white/40"> · {money(perDay)}/day</span>
            </span>
            <span className={`font-bold ${delta > 0 ? "text-emerald-300" : delta < 0 ? "text-red-300" : "text-white/60"}`} data-testid={`edit-dates-delta-${booking.id}`}>
              {delta > 0 ? "+" : ""}{money(delta)}
            </span>
          </div>
          <div className="flex items-center justify-between gap-3 text-xs mt-1.5 text-white/60">
            <span>Total {money(oldTotal)} → <b className="text-white">≈{money(newTotal)}</b> <span className="text-white/35">(estimate — exact total follows your package pricing on save)</span></span>
          </div>
          <label className="flex items-center gap-2 mt-2.5 text-xs text-white/80 cursor-pointer select-none" data-testid={`edit-dates-apply-${booking.id}`}>
            <input type="checkbox" checked={applyTotal} onChange={(e) => setApplyTotal(e.target.checked)} className="accent-[#F2B705] w-4 h-4" />
            Apply new total (recalculate price to match the new dates)
          </label>
          <p className="text-white/35 text-[10px] mt-1">Adjusts the base rental{waiverPd ? " + damage waiver" : ""}. One-time fees & discounts stay as-is — fine-tune with Adjust if needed.</p>
        </div>
      )}
      <div className="flex justify-end mt-3">
        <button data-testid={`edit-dates-save-${booking.id}`} onClick={() => save(false)} disabled={busy}
          className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md bg-[#F2B705] text-black text-sm font-semibold disabled:opacity-60">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save Dates
        </button>
      </div>
    </div>
  );
}
